import React from 'react';
import { Link } from 'react-router-dom';
import { LEGAL_ENTITY, ENTITY_FORMATION, LEGAL_EFFECTIVE_DATE } from '../data/legal';
import './Privacy.css';

const Privacy: React.FC = () => {
  const lastUpdated = LEGAL_EFFECTIVE_DATE;

  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <h1>Privacy Policy</h1>

        <div className="privacy-content">
          <p className="last-updated">
            <strong>Last Updated:</strong> {lastUpdated}
          </p>

          <section className="privacy-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Apres AI, a brand of {LEGAL_ENTITY}, {ENTITY_FORMATION} ("we," "our," or "us"). We are
              committed to protecting your privacy and ensuring the security of your personal information. This
              Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit
              our website https://apresai.dev.
            </p>
            <p>
              By using Apres AI, you agree to the collection and use of information in accordance with this policy.
              If you do not agree with our policies and practices, please do not use our services.
            </p>
          </section>

          <section className="privacy-section">
            <h2>2. Information We Collect</h2>

            <h3>2.1 Personal Information</h3>
            <p>We may collect the following types of personal information:</p>
            <ul>
              <li><strong>Account Information:</strong> When you create an account, we collect your name, email address, and authentication credentials through Google Sign-In.</li>
              <li><strong>Communication Data:</strong> Information from your interactions with our customer support team via support@apresai.dev.</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <ul>
              <li><strong>Usage Data:</strong> Pages viewed, features used, time spent on the site, and interaction patterns.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, device type, and IP address.</li>
              <li><strong>Cookies:</strong> We use cookies and similar tracking technologies to enhance your experience.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li><strong>Service Delivery:</strong> To provide our web and mobile application development services.</li>
              <li><strong>Account Management:</strong> To create and manage your account and authenticate users.</li>
              <li><strong>Communication:</strong> To respond to inquiries sent to support@apresai.dev and provide customer support.</li>
              <li><strong>Analytics:</strong> To understand how users interact with our website and improve functionality.</li>
              <li><strong>Security:</strong> To detect, prevent, and address technical issues, fraud, or security concerns.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>4. Third-Party Services</h2>

            <h3>4.1 Service Providers</h3>
            <p>We use third-party services to help operate our platform:</p>
            <ul>
              <li><strong>Google Sign-In:</strong> For secure authentication (Google Privacy Policy applies)</li>
              <li><strong>Amazon Web Services (AWS):</strong> For cloud hosting, email forwarding (SES), data storage, and infrastructure</li>
            </ul>

            <h3>4.2 Email Communication</h3>
            <p>
              When you send an email to support@apresai.dev, your message is received via Amazon SES (Simple Email Service)
              and forwarded to our team. Email messages are stored temporarily in AWS S3 for 7 days before automatic deletion.
            </p>
          </section>

          <section className="privacy-section">
            <h2>5. Cookies and Tracking Technologies</h2>

            <h3>5.1 Types of Cookies We Use</h3>
            <p><strong>Essential Cookies (Required):</strong></p>
            <ul>
              <li><strong>Authentication cookies:</strong> Remember your login status and session information via Google OAuth</li>
              <li><strong>Security cookies:</strong> Protect against fraud and maintain site security</li>
            </ul>

            <h3>5.2 Third-Party Cookies</h3>
            <ul>
              <li><strong>Google Sign-In:</strong> Uses authentication cookies for OAuth 2.0 login</li>
            </ul>

            <h3>5.3 Managing Cookies</h3>
            <p>
              Most browsers allow you to block or delete cookies through settings. Note that disabling essential
              cookies may affect website functionality.
            </p>
          </section>

          <section className="privacy-section">
            <h2>6. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your personal information:
            </p>
            <ul>
              <li>Encryption of data in transit using HTTPS/TLS</li>
              <li>Secure authentication via Google Sign-In OAuth 2.0</li>
              <li>Access controls limiting data access to authorized personnel only</li>
              <li>Regular security monitoring and updates</li>
            </ul>
            <p>
              However, no method of transmission over the internet or electronic storage is 100% secure. While we strive
              to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="privacy-section">
            <h2>7. Your Rights and Choices</h2>
            <p>You have the following rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> You can request a copy of the personal information we hold about you.</li>
              <li><strong>Correction:</strong> You can update your account information through your Google account.</li>
              <li><strong>Deletion:</strong> You can request deletion of your account and associated data by contacting us.</li>
              <li><strong>Cookie Control:</strong> You can manage cookie preferences through your browser settings.</li>
            </ul>
            <p>
              To exercise these rights, please contact us at <a href="mailto:support@apresai.dev">support@apresai.dev</a>.
            </p>
          </section>

          <section className="privacy-section">
            <h2>8. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to provide our services. Specifically:
            </p>
            <ul>
              <li>Account information: Until you delete your account or request deletion</li>
              <li>Support emails: Stored in S3 for 7 days, then automatically deleted</li>
              <li>Usage analytics: Aggregated data may be retained for improvement purposes</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>9. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in the United States where our servers (AWS) are located.
              By using Apres AI, you consent to the transfer of your information to the United States.
            </p>
          </section>

          <section className="privacy-section">
            <h2>10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically to reflect changes in our practices, technology, or legal requirements.
              We will notify you of material changes by:
            </p>
            <ul>
              <li>Posting a prominent notice on our website</li>
              <li>Updating the "Last Updated" date at the top of this policy</li>
            </ul>
            <p>
              Your continued use of Apres AI after changes are posted constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="privacy-section">
            <h2>11. Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="contact-box">
              <p><strong>Email:</strong> <a href="mailto:support@apresai.dev">support@apresai.dev</a></p>
              <p><strong>Website:</strong> <a href="https://apresai.dev">https://apresai.dev</a></p>
              <p><strong>Support Page:</strong> <Link to="/support">Visit our Support page</Link></p>
            </div>
            <p className="response-time">
              We will respond to your inquiry within 5 business days.
            </p>
          </section>

          <div className="policy-footer">
            <p>
              This Privacy Policy was last updated on <strong>{lastUpdated}</strong>.
            </p>
            <p className="footer-links">
              <Link to="/">Return to Home</Link> · <Link to="/about">About Us</Link> · <Link to="/support">Support</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
